import React from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import IconButton from '@mui/material/IconButton';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import DatePicker from '../../../shared/DatePicker';
import useSwalWrapper from '@jumbo/vendors/sweetalert2/hooks';
import useSweetAlertStyles from '@jumbo/vendors/sweetalert2/style';
import { adalApiFetch, userInfo } from '../../../../adalConfig';
import { numberFormat } from '../../../utils/numberFunctions';
import dayjs from 'dayjs';
import { textfieldDateFormat } from 'app/utils/dateFunctions';

const nonEditableStatuses = new Set(['Matched', 'Approved', 'Rejected']);
const purchaseReceiptSubAccountMyobId = 'PLP0000';
const billLineUnitPriceFractionDigits = 4;

const createLocalLineKey = (() => {
    let nextKey = -1;
    return () => {
        const value = nextKey;
        nextKey -= 1;
        return value;
    };
})();

const formatBillLineUnitPriceInput = (value) => {
    if (value === '' || value === null || value === undefined) {
        return '';
    }

    const parsedValue = Number(value);
    return Number.isFinite(parsedValue) ? parsedValue.toFixed(billLineUnitPriceFractionDigits) : value;
};

const normaliseLine = (line) => ({
    localId: line?.id > 0 ? line.id : createLocalLineKey(),
    id: line?.id ?? 0,
    description: line?.description ?? '',
    quantity: line?.quantity ?? '',
    unitPrice: formatBillLineUnitPriceInput(line?.unitPrice),
    totalPrice: line?.totalPrice ?? '',
    isGSTFree: Boolean(line?.isGSTFree ?? line?.isGstFree ?? false),
    prAdditional: Boolean(line?.prAdditional ?? line?.PRAdditional ?? false),
    accountId: line?.accountId > 0 ? line.accountId : null,
    subAccountId: line?.subAccountId > 0 ? line.subAccountId : null,
});

const createNewLine = (prAdditional = false) => ({
    localId: createLocalLineKey(),
    id: 0,
    description: '',
    quantity: '',
    unitPrice: '',
    totalPrice: '',
    isGSTFree: false,
    prAdditional,
    accountId: null,
    subAccountId: null,
});

const arraysEqual = (left, right) => left.length === right.length && left.every((value, index) => value === right[index]);

const getMatchedReceiptIds = (receipts) => (Array.isArray(receipts) ? receipts : [])
    .filter((receipt) => Boolean(receipt?.isMatchedToBill))
    .map((receipt) => receipt.id);

const sortOptions = (list) => [...list].sort((left, right) => {
    const leftDescription = left?.description ?? left?.name ?? '';
    const rightDescription = right?.description ?? right?.name ?? '';
    return leftDescription.localeCompare(rightDescription);
});

let selectorLookupPromise = null;

const loadSelectorLookups = (uri) => {
    if (!selectorLookupPromise) {
        selectorLookupPromise = Promise.all([
            adalApiFetch(fetch, `${uri}/APBills/GetVendors`),
            adalApiFetch(fetch, `${uri}/APBills/GetAccounts`),
            adalApiFetch(fetch, `${uri}/APBills/GetSubAccounts`),
            adalApiFetch(fetch, `${uri}/Roles/Get`),
            adalApiFetch(fetch, `${uri}/RoleAccounts/Get`),
        ])
            .then(([vendorsResponse, accountsResponse, subAccountsResponse, rolesResponse, approvalMapsResponse]) => Promise.all([
                vendorsResponse.json(),
                accountsResponse.json(),
                subAccountsResponse.json(),
                rolesResponse.json(),
                approvalMapsResponse.json(),
            ]))
            .then(([vendorData, accountData, subAccountData, roleData, approvalMapData]) => ({
                vendors: sortOptions(Array.isArray(vendorData) ? vendorData : []),
                accounts: sortOptions(Array.isArray(accountData) ? accountData : []),
                subAccounts: sortOptions(Array.isArray(subAccountData) ? subAccountData : []),
                roles: sortOptions(Array.isArray(roleData) ? roleData : []),
                approvalMaps: Array.isArray(approvalMapData) ? approvalMapData : [],
            }))
            .catch((err) => {
                selectorLookupPromise = null;
                throw err;
            });
    }

    return selectorLookupPromise;
};

const formatDateForRequest = (value) => {
    const normalizedDate = value ? dayjs(value) : null;
    return normalizedDate?.isValid() ? textfieldDateFormat(normalizedDate.toDate()) : null;
};

const getOptionById = (options, id) => options.find((option) => option.id === id) ?? null;
const getSubAccountMyobId = (option) => String(option?.myobid ?? option?.MYOBID ?? '').trim().toUpperCase();
const getAccountingOptionLabel = (option) => String(option?.description ?? option?.Description ?? option?.name ?? option?.Name ?? '').trim();
const getAccountingOptionSearchText = (option) => {
    const description = getAccountingOptionLabel(option);
    const myobId = String(option?.myobid ?? option?.MYOBID ?? option?.myobId ?? '').trim();

    return [description, myobId].filter(Boolean).join(' ').toLowerCase();
};
const filterAccountingOptions = (options, state) => {
    const searchTerms = String(state?.inputValue ?? '').trim().toLowerCase().split(/\s+/).filter(Boolean);

    if (searchTerms.length === 0) {
        return options;
    }
    console.log(searchTerms)
    return options.filter((option) => {
        const searchText = getAccountingOptionSearchText(option);
        return searchTerms.every((term) => searchText.includes(term));
    });
};
const renderAccountingOption = (props, option) => (
    <li {...props} key={option.id}>
        {getAccountingOptionLabel(option)}
    </li>
);

const getVendorOptionLabel = (option) => {
    if (!option) {
        return '';
    }

    const vendorName = option?.name ?? '';
    const vendorMyobId = option?.myobid ? ` (${option.myobid})` : '';
    return `${vendorName}${vendorMyobId}`;
};

const parseDecimal = (value) => {
    if (value === '' || value === null || value === undefined) {
        return null;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
};

const normaliseAbn = (value) => String(value ?? '').replace(/\D/g, '');

const getRoleOptionLabel = (option) => {
    if (!option) {
        return '';
    }

    const roleName = option?.name ?? '';
    const roleUserId = option?.userId ? ` (${option.userId})` : '';
    return `${roleName}${roleUserId}`;
};

const getApprovalMapPriority = (line, map) => {
    const lineAccountId = line?.accountId ?? null;
    const lineSubAccountId = line?.subAccountId ?? null;
    const mapAccountId = map?.accountId ?? null;
    const mapSubAccountId = map?.subAccountId ?? null;
    const hasMapAccount = (mapAccountId ?? 0) > 0;
    const hasMapSubAccount = (mapSubAccountId ?? 0) > 0;
    const isFallbackSubAccount = hasMapSubAccount && ((map?.subAccount?.myobid ?? map?.subAccount?.MYOBID ?? '').toUpperCase() === 'CFI0000');
    const accountMatches = hasMapAccount && mapAccountId === lineAccountId;
    const subAccountMatches = hasMapSubAccount && mapSubAccountId === lineSubAccountId;

    if (hasMapAccount && hasMapSubAccount) {
        return accountMatches && subAccountMatches ? 4 : 0;
    }

    if (hasMapAccount) {
        return accountMatches ? 3 : 0;
    }

    if (hasMapSubAccount) {
        return isFallbackSubAccount ? 0 : (subAccountMatches ? 2 : 0);
    }

    return 0;
};

const getApprovalMapFallbackPriority = (map) => {
    const mapAccountId = map?.accountId ?? null;
    const mapSubAccountId = map?.subAccountId ?? null;
    const hasMapAccount = (mapAccountId ?? 0) > 0;
    const hasMapSubAccount = (mapSubAccountId ?? 0) > 0;
    const isFallbackSubAccount = hasMapSubAccount && ((map?.subAccount?.myobid ?? map?.subAccount?.MYOBID ?? '').toUpperCase() === 'CFI0000');

    return !hasMapAccount && isFallbackSubAccount ? 1 : 0;
};

const deriveApproverRoleId = (lines, approvalMaps) => {
    const lineList = (Array.isArray(lines) ? lines : []).filter((line) => {
        const accountId = line?.accountId ?? null;
        const subAccountId = line?.subAccountId ?? null;
        return (accountId ?? 0) > 0 || (subAccountId ?? 0) > 0;
    });
    const approvalMapList = Array.isArray(approvalMaps) ? approvalMaps : [];

    if (lineList.length === 0) {
        return null;
    }

    let bestPriority = 0;
    let bestRoleId = null;

    for (const map of approvalMapList) {
        let matchPriority = getApprovalMapFallbackPriority(map);

        for (const line of lineList) {
            matchPriority = Math.max(matchPriority, getApprovalMapPriority(line, map));

            if (matchPriority === 4) {
                break;
            }
        }

        if (matchPriority > bestPriority && (map?.roleId ?? 0) > 0) {
            bestPriority = matchPriority;
            bestRoleId = map.roleId;
        }
    }

    return bestRoleId;
};

const calculateLineTotal = (quantity, unitPrice) => {
    const parsedQuantity = parseDecimal(quantity);
    const parsedUnitPrice = parseDecimal(unitPrice);

    if (parsedQuantity === null || parsedUnitPrice === null) {
        return '';
    }

    return (parsedQuantity * parsedUnitPrice).toFixed(2);
};

const calculateDerivedValue = (numerator, denominator, fractionDigits = 2) => {
    const parsedNumerator = parseDecimal(numerator);
    const parsedDenominator = parseDecimal(denominator);

    if (parsedNumerator === null || parsedDenominator === null || parsedDenominator === 0) {
        return '';
    }

    return (parsedNumerator / parsedDenominator).toFixed(fractionDigits);
};

const canEditBill = (status) => {
    const normalisedStatus = (status ?? '').trim();
    return normalisedStatus === '' || !nonEditableStatuses.has(normalisedStatus);
};

const formatSummaryNumber = (value) => value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

const formatCurrencyWithNumberFormat = (value, fractionDigits = 2) => {
    const parsedValue = parseDecimal(value);

    if (parsedValue === null) {
        return '';
    }

    return `$${numberFormat(parsedValue.toFixed(fractionDigits))}`;
};

const formatBillLineUnitPrice = (value) => formatCurrencyWithNumberFormat(value, billLineUnitPriceFractionDigits);

const totalsMatch = (left, right) => Math.abs((left ?? 0) - (right ?? 0)) < 0.01;

const getBillLineMismatchDetails = (line) => {
    const quantity = parseDecimal(line?.quantity);
    const unitPrice = parseDecimal(line?.unitPrice);
    const totalPrice = parseDecimal(line?.totalPrice);

    if (quantity === null || unitPrice === null || totalPrice === null) {
        return null;
    }

    const expectedTotal = quantity * unitPrice;

    if (totalsMatch(expectedTotal, totalPrice)) {
        return null;
    }

    return {
        expectedTotal,
        totalPrice,
        message: `Qty ${formatSummaryNumber(quantity)} x ${formatBillLineUnitPrice(unitPrice)} should equal ${formatCurrencyWithNumberFormat(expectedTotal)}, but the line total is ${formatCurrencyWithNumberFormat(totalPrice)}.`
    };
};

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    bgcolor: 'background.paper',
    boxShadow: 24,
    width: '92%',
    maxWidth: 1280,
    maxHeight: '90vh',
    overflow: 'auto',
    borderRadius: 2,
    p: 3,
};

const billLineGridColumns = '60px minmax(260px, 2fr) 120px 140px 140px minmax(240px, 1.1fr) minmax(240px, 1.1fr) 120px';
const billLineEditorGridColumns = `${billLineGridColumns} 110px`;
const billLinePreviewBatchSize = 75;
const billLineEditorBatchSize = 100;
const lineScrollLoadThreshold = 260;

const EditBill = (props) => {
    const uri = process.env.REACT_APP_BASE_URL;
    const billId = props.data?.id ?? 0;
    const Swal = useSwalWrapper();
    const sweetAlertStyles = useSweetAlertStyles();
    const successAlertCustomClass = {
        confirmButton: `${sweetAlertStyles.confirmButton} ${sweetAlertStyles.confirmButtonLarge}`
    };
    const [user] = React.useState(userInfo());
    const [abn, setAbn] = React.useState(props.data?.abn || '');
    const [supplierRef, setSupplierRef] = React.useState(props.data?.supplierRef || '');
    const [description, setDescription] = React.useState(props.data?.description ?? props.data?.Description ?? '');
    const [vendorId, setVendorId] = React.useState(props.data?.vendorId ?? props.data?.vendor?.id ?? null);
    const [invoiceDate, setInvoiceDate] = React.useState(props.data?.date ? dayjs(props.data.date) : null);
    const [dueDate, setDueDate] = React.useState(props.data?.dueDate ? dayjs(props.data.dueDate) : null);
    const [net, setNet] = React.useState(props.data?.net ?? '');
    const [tax, setTax] = React.useState(props.data?.tax ?? '');
    const [billLines, setBillLines] = React.useState(Array.isArray(props.data?.billLines) ? props.data.billLines.map(normaliseLine) : []);
    const [vendors, setVendors] = React.useState([]);
    const [accounts, setAccounts] = React.useState([]);
    const [subAccounts, setSubAccounts] = React.useState([]);
    const [roles, setRoles] = React.useState([]);
    const [approvalMaps, setApprovalMaps] = React.useState([]);
    const [approverRoleId, setApproverRoleId] = React.useState(props.data?.approver ?? props.data?.Approver ?? null);
    const [bulkAccountId, setBulkAccountId] = React.useState(null);
    const [bulkSubAccountId, setBulkSubAccountId] = React.useState(null);
    const [additionalTaxPercent, setAdditionalTaxPercent] = React.useState('10');
    const [loading, setLoading] = React.useState(false);
    const [pushingToMyob, setPushingToMyob] = React.useState(false);
    const [submittingForApproval, setSubmittingForApproval] = React.useState(false);
    const [workflowAction, setWorkflowAction] = React.useState('');
    const [linesLoading, setLinesLoading] = React.useState(false);
    const [selectorsLoading, setSelectorsLoading] = React.useState(false);
    const [lineEditorOpen, setLineEditorOpen] = React.useState(false);
    const [visiblePreviewLineCount, setVisiblePreviewLineCount] = React.useState(billLinePreviewBatchSize);
    const [visibleEditorLineCount, setVisibleEditorLineCount] = React.useState(billLineEditorBatchSize);
    const [purchaseReceiptModalOpen, setPurchaseReceiptModalOpen] = React.useState(false);
    const [purchaseReceipts, setPurchaseReceipts] = React.useState([]);
    const [purchaseReceiptsLoading, setPurchaseReceiptsLoading] = React.useState(false);
    const [selectedReceiptId, setSelectedReceiptId] = React.useState(null);
    const [pendingMatchedReceiptIds, setPendingMatchedReceiptIds] = React.useState([]);
    const [selectedReceiptDetails, setSelectedReceiptDetails] = React.useState(null);
    const [receiptDetailsLoading, setReceiptDetailsLoading] = React.useState(false);
    const [matchedReceiptIds, setMatchedReceiptIds] = React.useState([]);
    const [matchedPRDetails, setMatchedPRDetails] = React.useState([]);
    const [matchedPRDetailsLoading, setMatchedPRDetailsLoading] = React.useState(false);
    const isWorkflow3 = String(props.workflowId ?? '') === '3';
    const isWorkflow4 = String(props.workflowId ?? '') === '4';
    const isWorkflow3Admin = Boolean(props.canEditWorkflow3);
    const [workflow3EditMode, setWorkflow3EditMode] = React.useState(false);
    const canEditWorkflow3 = isWorkflow3 && isWorkflow3Admin && workflow3EditMode;
    const canEditDescription = isWorkflow3 || canEditBill(props.data?.status);
    const canEditLines = canEditBill(props.data?.status) && (!isWorkflow3 || canEditWorkflow3);
    const selectedVendor = React.useMemo(() => {
        const fromList = vendors.find((v) => v.id === vendorId);
        if (fromList) {
            return fromList;
        }

        if (props.data?.vendor?.id === vendorId) {
            return props.data.vendor;
        }

        return null;
    }, [vendors, vendorId, props.data]);
    const selectedVendorAbn = normaliseAbn(selectedVendor?.abn ?? selectedVendor?.ABN);
    const hasAbnMismatch = Boolean(selectedVendorAbn) && normaliseAbn(abn) !== selectedVendorAbn;
    const selectedVendorType = (selectedVendor?.type ?? '').trim().toUpperCase();
    const requiresPRForApproval = selectedVendorType === 'FARMER' || selectedVendorType === 'SPATFARMER';
    const matchedReceiptLookup = React.useMemo(() => new Map(purchaseReceipts.map((receipt) => [receipt.id, receipt])), [purchaseReceipts]);
    const matchedReceiptOptions = React.useMemo(
        () => matchedReceiptIds.map((receiptId) => matchedReceiptLookup.get(receiptId)).filter(Boolean),
        [matchedReceiptIds, matchedReceiptLookup]
    );
    const matchedReceiptMyobIds = React.useMemo(
        () => matchedReceiptOptions.map((receipt) => receipt.myobid),
        [matchedReceiptOptions]
    );
    const hasMatchedPurchaseReceipts = matchedReceiptIds.length > 0;
    const editableBillLines = React.useMemo(
        () => (hasMatchedPurchaseReceipts ? billLines.filter((line) => line.prAdditional) : billLines),
        [billLines, hasMatchedPurchaseReceipts]
    );
    const previewBillLines = React.useMemo(() => billLines.slice(0, visiblePreviewLineCount), [billLines, visiblePreviewLineCount]);
    const previewEditableBillLines = React.useMemo(() => editableBillLines.slice(0, visiblePreviewLineCount), [editableBillLines, visiblePreviewLineCount]);
    const visibleEditorBillLines = React.useMemo(
        () => editableBillLines.slice(0, visibleEditorLineCount),
        [editableBillLines, visibleEditorLineCount]
    );
    const matchedReceiptLineCount = React.useMemo(
        () => matchedPRDetails.reduce((runningTotal, receipt) => runningTotal + ((receipt?.lines ?? []).length), 0),
        [matchedPRDetails]
    );
    const matchedReceiptQuantityTotal = React.useMemo(
        () => matchedPRDetails.reduce((runningTotal, receipt) => runningTotal + (receipt?.lines ?? []).reduce((sum, line) => sum + (line.quantity ?? 0), 0), 0),
        [matchedPRDetails]
    );
    const matchedReceiptValueTotal = React.useMemo(
        () => matchedPRDetails.reduce((runningTotal, receipt) => runningTotal + (receipt?.lines ?? []).reduce((sum, line) => sum + ((line.quantity ?? 0) * (line.unitCost ?? 0)), 0), 0),
        [matchedPRDetails]
    );
    const additionalLineQuantityTotal = editableBillLines.reduce((runningTotal, line) => runningTotal + (parseDecimal(line.quantity) ?? 0), 0);
    const additionalLineValueTotal = editableBillLines.reduce((runningTotal, line) => runningTotal + (parseDecimal(line.totalPrice) ?? 0), 0);
    const lineCount = hasMatchedPurchaseReceipts ? matchedReceiptLineCount + editableBillLines.length : billLines.length;
    const quantityTotal = hasMatchedPurchaseReceipts
        ? matchedReceiptQuantityTotal + additionalLineQuantityTotal
        : billLines.reduce((runningTotal, line) => runningTotal + (parseDecimal(line.quantity) ?? 0), 0);
    const lineValueTotal = hasMatchedPurchaseReceipts
        ? matchedReceiptValueTotal + additionalLineValueTotal
        : billLines.reduce((runningTotal, line) => runningTotal + (parseDecimal(line.totalPrice) ?? 0), 0);
    const billTotal = parseDecimal((parseDecimal(net) ?? 0) + (parseDecimal(tax) ?? 0));
    const showLineTotalWarning = billTotal !== null && !totalsMatch(lineValueTotal, billTotal);
    const approvedByUser = props.data?.approvedBy ?? props.data?.ApprovedBy ?? '';
    const storedApproverValue = props.data?.approver ?? props.data?.Approver ?? null;
    const storedApproverRoleId = Number(storedApproverValue) > 0 ? Number(storedApproverValue) : null;
    const pdfPopupRef = React.useRef(null);
    const initialApproverSyncRef = React.useRef(false);
    const approverManuallySelectedRef = React.useRef(false);

    React.useEffect(() => {
        setAbn(props.data?.abn || '');
        setSupplierRef(props.data?.supplierRef || '');
        setVendorId(props.data?.vendorId ?? props.data?.vendor?.id ?? null);
        setDescription(props.data?.description ?? props.data?.Description ?? '');
        setInvoiceDate(props.data?.date ? dayjs(props.data.date) : null);
        setDueDate(props.data?.dueDate ? dayjs(props.data.dueDate) : null);
        setNet(props.data?.net ?? '');
        setTax(props.data?.tax ?? '');
        setBillLines(Array.isArray(props.data?.billLines) ? props.data.billLines.map(normaliseLine) : []);
        setApproverRoleId(storedApproverRoleId);
        setBulkAccountId(null);
        setBulkSubAccountId(null);
        setAdditionalTaxPercent('10');
        setLineEditorOpen(false);
        setVisiblePreviewLineCount(billLinePreviewBatchSize);
        setVisibleEditorLineCount(billLineEditorBatchSize);
        setPurchaseReceiptModalOpen(false);
        setPurchaseReceipts([]);
        setSelectedReceiptId(null);
        setPendingMatchedReceiptIds([]);
        setMatchedReceiptIds([]);
        setSelectedReceiptDetails(null);
        setMatchedPRDetails([]);
        setWorkflow3EditMode(false);
        initialApproverSyncRef.current = false;
        approverManuallySelectedRef.current = false;
    }, [props.data, storedApproverRoleId]);

    React.useEffect(() => {
        let active = true;

        const loadPurchaseReceipts = async () => {
            if (!vendorId) {
                setPurchaseReceipts([]);
                setSelectedReceiptId(null);
                setPendingMatchedReceiptIds([]);
                setMatchedReceiptIds([]);
                setSelectedReceiptDetails(null);
                return;
            }

            setPurchaseReceiptsLoading(true);
            try {
                const response = await adalApiFetch(
                    fetch,
                    `${uri}/APBills/GetPurchaseReceipts?` + new URLSearchParams({
                        vendorId: String(vendorId),
                        billId: String(billId || 0),
                    })
                );
                const data = await response.json();

                if (!active) {
                    return;
                }

                const receipts = Array.isArray(data) ? data : [];
                const nextMatchedReceiptIds = getMatchedReceiptIds(receipts);

                setPurchaseReceipts(receipts);

                setMatchedReceiptIds((currentIds) => (arraysEqual(currentIds, nextMatchedReceiptIds) ? currentIds : nextMatchedReceiptIds));
                setPendingMatchedReceiptIds(nextMatchedReceiptIds);

                setSelectedReceiptId((currentSelectedReceiptId) => (
                    currentSelectedReceiptId && receipts.some((receipt) => receipt.id === currentSelectedReceiptId)
                        ? currentSelectedReceiptId
                        : (nextMatchedReceiptIds[0] ?? receipts[0]?.id ?? null)
                ));
            } catch (err) {
                console.error(err);
            } finally {
                if (active) {
                    setPurchaseReceiptsLoading(false);
                }
            }
        };

        loadPurchaseReceipts();

        return () => {
            active = false;
        };
    }, [vendorId, billId, uri]);

    React.useEffect(() => {
        let active = true;

        const loadPurchaseReceiptDetails = async () => {
            if (!selectedReceiptId) {
                setSelectedReceiptDetails(null);
                return;
            }

            setReceiptDetailsLoading(true);
            try {
                const response = await adalApiFetch(
                    fetch,
                    `${uri}/APBills/GetPurchaseReceiptDetails?` + new URLSearchParams({ receiptId: String(selectedReceiptId) })
                );
                const data = await response.json();

                if (!active) {
                    return;
                }

                setSelectedReceiptDetails(data);
            } catch (err) {
                console.error(err);
                if (active) {
                    Swal.fire({
                        icon: 'error',
                        title: 'PR details unavailable',
                        text: 'Unable to load purchase receipt details.'
                    });
                }
            } finally {
                if (active) {
                    setReceiptDetailsLoading(false);
                }
            }
        };

        loadPurchaseReceiptDetails();

        return () => {
            active = false;
        };
    }, [selectedReceiptId, uri, Swal]);

    React.useEffect(() => {
        let active = true;

        const loadMatchedPRDetails = async () => {
            if (matchedReceiptIds.length === 0) {
                setMatchedPRDetails([]);
                return;
            }

            setMatchedPRDetailsLoading(true);
            try {
                const receiptDetails = await Promise.all(matchedReceiptIds.map(async (receiptId) => {
                    const response = await adalApiFetch(
                        fetch,
                        `${uri}/APBills/GetPurchaseReceiptDetails?` + new URLSearchParams({ receiptId: String(receiptId) })
                    );

                    return response.json();
                }));

                if (!active) return;

                setMatchedPRDetails(receiptDetails.filter(Boolean));
            } catch (err) {
                console.error(err);
            } finally {
                if (active) setMatchedPRDetailsLoading(false);
            }
        };

        loadMatchedPRDetails();

        return () => { active = false; };
    }, [matchedReceiptIds, uri]);

    React.useEffect(() => {
        let active = true;

        const loadSelectors = async () => {
            setSelectorsLoading(true);
            try {
                const selectorData = await loadSelectorLookups(uri);

                if (!active) {
                    return;
                }

                setVendors(selectorData.vendors);
                setAccounts(selectorData.accounts);
                setSubAccounts(selectorData.subAccounts);
                setRoles(selectorData.roles);
                setApprovalMaps(selectorData.approvalMaps);
            } catch (err) {
                console.error(err);
                if (active) {
                    Swal.fire({
                        icon: 'error',
                        title: 'Lookup load failed',
                        text: 'Unable to load account selections for this bill.'
                    });
                }
            } finally {
                if (active) {
                    setSelectorsLoading(false);
                }
            }
        };

        loadSelectors();

        return () => {
            active = false;
        };
    }, [uri, Swal]);

    React.useEffect(() => {
        if (!hasMatchedPurchaseReceipts || editableBillLines.length === 0) {
            return;
        }

        const purchaseReceiptSubAccountId = subAccounts.find(
            (option) => getSubAccountMyobId(option) === purchaseReceiptSubAccountMyobId
        )?.id ?? null;

        if (!purchaseReceiptSubAccountId) {
            return;
        }

        if (bulkSubAccountId !== purchaseReceiptSubAccountId) {
            setBulkSubAccountId(purchaseReceiptSubAccountId);
        }

        if (editableBillLines.every((line) => line.subAccountId === purchaseReceiptSubAccountId)) {
            return;
        }

        setBillLines((currentLines) => currentLines.map((line) => ({
            ...line,
            subAccountId: line.prAdditional ? purchaseReceiptSubAccountId : line.subAccountId,
        })));
    }, [hasMatchedPurchaseReceipts, subAccounts, editableBillLines, bulkSubAccountId]);

    React.useEffect(() => {
        if (approvalMaps.length === 0) {
            return;
        }

        if (approverManuallySelectedRef.current) {
            return;
        }

        if (storedApproverRoleId !== null) {
            initialApproverSyncRef.current = true;
            return;
        }

        const derivedApproverRoleId = deriveApproverRoleId(billLines, approvalMaps);
        if (!initialApproverSyncRef.current) {
            initialApproverSyncRef.current = true;
            setApproverRoleId(derivedApproverRoleId);
            return;
        }

        setApproverRoleId(derivedApproverRoleId);
    }, [approvalMaps, billLines, storedApproverRoleId]);

    React.useEffect(() => {
        let active = true;

        const loadBillLines = async () => {
            if (!billId) {
                setBillLines([]);
                return;
            }

            setLinesLoading(true);
            try {
                const response = await adalApiFetch(
                    fetch,
                    `${uri}/APBills/GetLines?` + new URLSearchParams({ BillId: billId })
                );
                const data = await response.json();

                if (!active) {
                    return;
                }

                setBillLines(Array.isArray(data) ? data.map(normaliseLine) : []);
            } catch (err) {
                console.error(err);
            } finally {
                if (active) {
                    setLinesLoading(false);
                }
            }
        };

        loadBillLines();

        return () => {
            active = false;
        };
    }, [billId, uri]);

    const updateBillLine = (lineLocalId, field, value) => {
        if (field === 'accountId') {
            setBulkAccountId(null);
        }

        if (field === 'subAccountId') {
            setBulkSubAccountId(null);
        }

        setBillLines((currentLines) => currentLines.map((line) => {
            if (line.localId !== lineLocalId) {
                return line;
            }

            if (field === 'quantity') {
                const recalculatedTotal = calculateLineTotal(value, line.unitPrice);

                return {
                    ...line,
                    quantity: value,
                    unitPrice: recalculatedTotal === '' ? calculateDerivedValue(line.totalPrice, value, billLineUnitPriceFractionDigits) : line.unitPrice,
                    totalPrice: recalculatedTotal === '' ? line.totalPrice : recalculatedTotal,
                };
            }

            if (field === 'unitPrice') {
                const recalculatedTotal = calculateLineTotal(line.quantity, value);

                return {
                    ...line,
                    unitPrice: value,
                    quantity: recalculatedTotal === '' ? calculateDerivedValue(line.totalPrice, value) : line.quantity,
                    totalPrice: recalculatedTotal === '' ? line.totalPrice : recalculatedTotal,
                };
            }

            if (field === 'totalPrice') {
                return {
                    ...line,
                    totalPrice: value,
                    unitPrice: parseDecimal(line.quantity) !== null && parseDecimal(line.quantity) !== 0
                        ? calculateDerivedValue(value, line.quantity, billLineUnitPriceFractionDigits)
                        : line.unitPrice,
                    quantity: (parseDecimal(line.quantity) === null || parseDecimal(line.quantity) === 0) && parseDecimal(line.unitPrice) !== null && parseDecimal(line.unitPrice) !== 0
                        ? calculateDerivedValue(value, line.unitPrice)
                        : line.quantity,
                };
            }

            return {
                ...line,
                [field]: value,
            };
        }));
    };

    const applyAdditionalTaxToLines = () => {
        const taxPercent = parseDecimal(additionalTaxPercent);
        if (taxPercent === null) {
            Swal.fire({
                icon: 'error',
                title: 'Invalid tax value',
                text: 'Additional tax must be a valid number.'
            });
            return;
        }

        const taxMultiplier = 1 + (taxPercent / 100);
        setBillLines((currentLines) => currentLines.map((line) => {
            if (hasMatchedPurchaseReceipts && !line.prAdditional) {
                return line;
            }

            const currentTotal = parseDecimal(line.totalPrice);
            const baseTotal = currentTotal ?? parseDecimal(calculateLineTotal(line.quantity, line.unitPrice));

            if (baseTotal === null) {
                return line;
            }

            const nextTotal = (baseTotal * taxMultiplier).toFixed(2);
            const parsedQuantity = parseDecimal(line.quantity);

            return {
                ...line,
                totalPrice: nextTotal,
                unitPrice: parsedQuantity !== null && parsedQuantity !== 0
                    ? calculateDerivedValue(nextTotal, line.quantity, billLineUnitPriceFractionDigits)
                    : line.unitPrice,
            };
        }));
    };

    const addBillLine = () => {
        setBillLines((currentLines) => [...currentLines, createNewLine(hasMatchedPurchaseReceipts)]);
    };

    const deleteBillLine = (lineLocalId) => {
        setBillLines((currentLines) => currentLines.filter((line) => line.localId !== lineLocalId));
    };

    const handlePreviewLineScroll = React.useCallback((event) => {
        const target = event.currentTarget;
        const nearBottom = target.scrollHeight - target.scrollTop - target.clientHeight < lineScrollLoadThreshold;

        if (!nearBottom) {
            return;
        }

        const totalVisibleLines = hasMatchedPurchaseReceipts ? editableBillLines.length : billLines.length;
        setVisiblePreviewLineCount((current) => Math.min(current + billLinePreviewBatchSize, totalVisibleLines));
    }, [billLines.length, editableBillLines.length, hasMatchedPurchaseReceipts]);

    const handleEditorLineScroll = React.useCallback((event) => {
        const target = event.currentTarget;
        const nearBottom = target.scrollHeight - target.scrollTop - target.clientHeight < lineScrollLoadThreshold;

        if (!nearBottom) {
            return;
        }

        setVisibleEditorLineCount((current) => Math.min(current + billLineEditorBatchSize, editableBillLines.length));
    }, [editableBillLines.length]);

    const openPdfPopup = React.useCallback(() => {
        if (!props.data?.uri) {
            return;
        }

        const popup = window.open(
            props.data.uri,
            `bill-pdf-${billId}`,
            'popup=yes,width=1200,height=900,resizable=yes,scrollbars=yes'
        );

        if (!popup) {
            Swal.fire({
                icon: 'warning',
                title: 'PDF popup blocked',
                text: 'Allow popups for this site to open the bill PDF in a separate window.'
            });
            return;
        }

        popup.focus();
        pdfPopupRef.current = popup;
    }, [Swal, billId, props.data?.uri]);

    const handleOpenLineEditor = () => {
        setVisiblePreviewLineCount(billLinePreviewBatchSize);
        setVisibleEditorLineCount(billLineEditorBatchSize);
        setLineEditorOpen(true);
        window.requestAnimationFrame(() => {
            window.setTimeout(openPdfPopup, 0);
        });
    };

    const handleCloseLineEditor = () => {
        setLineEditorOpen(false);
        if (pdfPopupRef.current && !pdfPopupRef.current.closed) {
            pdfPopupRef.current.focus();
        }
    };

    const openPurchaseReceiptModal = () => {
        if (!vendorId) {
            Swal.fire({
                icon: 'warning',
                title: 'Vendor required',
                text: 'Select a vendor first to load matching purchase receipts.'
            });
            return;
        }

        const fallbackReceipt = purchaseReceipts.find((receipt) => matchedReceiptIds.includes(receipt.id)) ?? purchaseReceipts[0] ?? null;

        setPendingMatchedReceiptIds(matchedReceiptIds);
        setSelectedReceiptId(selectedReceiptId ?? fallbackReceipt?.id ?? null);
        setPurchaseReceiptModalOpen(true);
    };

    const closePurchaseReceiptModal = () => {
        setPurchaseReceiptModalOpen(false);
    };

    const togglePendingPurchaseReceipt = (receiptId) => {
        setPendingMatchedReceiptIds((currentIds) => (
            currentIds.includes(receiptId)
                ? currentIds.filter((currentId) => currentId !== receiptId)
                : [...currentIds, receiptId]
        ));
    };

    const applySelectedPurchaseReceipt = () => {
        setMatchedReceiptIds(pendingMatchedReceiptIds);
        setPurchaseReceiptModalOpen(false);
    };

    const validate = () => {
        const errors = [];
        const parsedNet = parseDecimal(net);
        const parsedTax = parseDecimal(tax);
        const trimmedDescription = String(description ?? '').trim();

        if (!abn) errors.push('ABN is required.');
        if (!supplierRef) errors.push('Supplier Reference is required.');
        if (!vendorId) errors.push('A vendor must be selected before saving. If the vendor is not listed, create it in MYOB first.');
        if (!dueDate) errors.push('Due Date is required.');
        if (parsedNet === null) errors.push('Net is required and must be a valid number.');
        if (parsedTax === null) errors.push('Tax is required and must be a valid number.');
        if (isWorkflow3 && !trimmedDescription) errors.push('Description is required on Awaiting Approval bills.');

        const linesRequiringAccounts = hasMatchedPurchaseReceipts ? editableBillLines : billLines;

        if (canEditLines && linesRequiringAccounts.some((line) => !line.accountId || !line.subAccountId)) {
            errors.push('Each bill line needs both an account and a sub account before saving.');
        }

        if (errors.length > 0) {
            Swal.fire({
                html: errors.join('<br/>'),
                icon: 'error',
                title: 'Validation error'
            });
            return false;
        }
        return true;
    };

    const submitBill = async ({ submitForApproval = false, showSuccess = true, reloadOnSuccess = true } = {}) => {
        if (!validate()) return false;

        if (submitForApproval && requiresPRForApproval && !hasMatchedPurchaseReceipts) {
            const response = await Swal.fire({
                icon: 'warning',
                title: 'No matched purchase receipts',
                text: 'This vendor is FARMER/SPATFARMER and has not been matched to a purchase receipt. Continue submitting for approval?',
                showCancelButton: true,
                confirmButtonText: 'Submit anyway',
                cancelButtonText: 'Cancel'
            });

            if (!response.isConfirmed && !response.value) {
                return false;
            }
        }

        setLoading(true);
        setSubmittingForApproval(submitForApproval);

        const payload = {
            User: user?.name,
            Id: billId,
            ABN: abn,
            SupplierRef: supplierRef,
            CustomerId: props.data?.customerId ?? props.data?.CustomerId ?? null,
            Description: description,
            VendorId: vendorId,
            PurchaseReceiptIds: matchedReceiptIds,
            Date: formatDateForRequest(invoiceDate),
            DueDate: formatDateForRequest(dueDate),
            Net: parseDecimal(net),
            Tax: parseDecimal(tax),
            Approver: approverRoleId && approverRoleId > 0 ? approverRoleId : null,
            Lines: billLines.map((line) => ({
                Id: line.id,
                Description: line.description,
                Quantity: parseDecimal(line.quantity),
                UnitPrice: parseDecimal(line.unitPrice),
                TotalPrice: parseDecimal(line.totalPrice),
                IsGSTFree: Boolean(line.isGSTFree),
                PRAdditional: Boolean(line.prAdditional),
                AccountId: line.accountId && line.accountId > 0 ? line.accountId : null,
                SubAccountId: line.subAccountId && line.subAccountId > 0 ? line.subAccountId : null,
            })),
        };

        try {
            await adalApiFetch(fetch, `${uri}/APBills/Update`, {
                method: 'post',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            if (submitForApproval) {
                await adalApiFetch(fetch, `${uri}/APBills/AwaitApproval/${billId}`, {
                    method: 'post',
                });
            }

            if (showSuccess) {
                Swal.fire({
                    icon: 'success',
                    title: submitForApproval ? 'Bill submitted for approval' : 'Bill updated',
                    confirmButtonText: 'OK',
                    customClass: successAlertCustomClass
                });
            }

            setLineEditorOpen(false);

            if (reloadOnSuccess && props.onSaved) {
                props.onSaved();
            }

            return true;
        } catch (err) {
            console.error(err);
            Swal.fire({
                icon: 'error',
                title: 'Update failed',
                text: 'An error occurred while updating the bill.'
            });

            return false;
        } finally {
            setLoading(false);
            setSubmittingForApproval(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        await submitBill({ submitForApproval: false });
    };

    const handleSaveAndSubmitForApproval = async () => {
        await submitBill({ submitForApproval: true });
    };

    const handlePushToMyob = async () => {
        if (!billId || !hasMatchedPurchaseReceipts) {
            return;
        }

        const response = await Swal.fire({
            icon: 'warning',
            title: 'Push to MYOB?',
            text: 'This will save the bill, approve it with your user, and push it directly to MYOB.',
            showCancelButton: true,
            confirmButtonText: 'Push to MYOB',
            cancelButtonText: 'Cancel'
        });

        if (!response.isConfirmed && !response.value) {
            return;
        }

        setPushingToMyob(true);

        try {
            const saveSucceeded = await submitBill({ showSuccess: false, reloadOnSuccess: false });
            if (!saveSucceeded) {
                return;
            }

            await handleWorkflowDecision('approve', {
                skipBillSave: true,
                successTitle: 'Bill pushed to MYOB',
                errorText: 'Unable to approve and push this bill to MYOB.'
            });
        } catch (err) {
            console.error(err);
            Swal.fire({
                icon: 'error',
                title: 'Push failed',
                text: 'Unable to approve and push this bill to MYOB.'
            });
        } finally {
            setLoading(false);
            setPushingToMyob(false);
        }
    };

    const handleVendorChange = async (newValue) => {
        const nextVendorId = newValue?.id ?? null;
        const nextVendorAbnValue = newValue?.abn ?? newValue?.ABN ?? '';
        const nextVendorAbn = normaliseAbn(nextVendorAbnValue);
        const currentAbn = normaliseAbn(abn);

        if (newValue && currentAbn && currentAbn !== nextVendorAbn) {
            const response = await Swal.fire({
                icon: 'warning',
                title: 'Replace ABN?',
                text: `Selecting ${newValue?.name ?? 'this vendor'} will replace the current ABN with ${nextVendorAbnValue || 'a blank value'}. Are you sure?`,
                showCancelButton: true,
                confirmButtonText: 'Yes',
                cancelButtonText: 'No'
            });

            if (!response.isConfirmed && !response.value) {
                return;
            }
        }

        setVendorId(nextVendorId);
        setAbn(nextVendorAbnValue);
    };

    const handleWorkflowDecision = async (decision, options = {}) => {
        if (!billId) {
            return false;
        }

        const {
            skipBillSave = false,
            successTitle,
            errorTitle = 'Update failed',
            errorText = 'Unable to update bill approval status.'
        } = options;

        if (decision === 'approve' && !vendorId) {
            Swal.fire({
                icon: 'error',
                title: 'Validation error',
                text: 'A vendor must be selected before approving. If the vendor is not listed, create it in MYOB first.'
            });
            return false;
        }

        if (decision === 'approve' && isWorkflow3 && !String(description ?? '').trim()) {
            Swal.fire({
                icon: 'error',
                title: 'Validation error',
                text: 'Description is required before approving an Awaiting Approval bill.'
            });
            return false;
        }

        if (decision === 'approve' && !skipBillSave) {
            const saveSucceeded = await submitBill({ showSuccess: false, reloadOnSuccess: false });
            if (!saveSucceeded) {
                return false;
            }
        }


        const userId = user?.userName || user?.name || '';
        if (!userId) {
            Swal.fire({
                icon: 'error',
                title: 'User unavailable',
                text: 'Unable to determine the signed-in user for approval.'
            });
            return false;
        }

        const decisionRoute = decision === 'approve' ? 'Approve' : 'Reject';

        setLoading(true);
        setWorkflowAction(decision);
        try {
            await adalApiFetch(fetch, `${uri}/APBills/${decisionRoute}/${billId}?` + new URLSearchParams({ UserId: userId }), {
                method: 'post',
            });

            Swal.fire({
                icon: 'success',
                title: successTitle ?? (decision === 'approve' ? 'Bill approved' : 'Bill rejected'),
                confirmButtonText: 'OK',
                customClass: successAlertCustomClass
            });

            if (props.onSaved) {
                props.onSaved();
            }

            return true;
        } catch (err) {
            console.error(err);
            Swal.fire({
                icon: 'error',
                title: errorTitle,
                text: errorText
            });

            return false;
        } finally {
            setLoading(false);
            setWorkflowAction('');
        }
    };

    return (
        <Box
            component="form"
            noValidate
            autoComplete="off"
            onSubmit={handleSubmit}
            sx={{ p: 2, width: '100%' }}
        >
            <Grid container spacing={2}>
                {isWorkflow3 && isWorkflow3Admin && (
                    <Grid item xs={12}>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <FormControlLabel
                                control={(
                                    <Switch
                                        checked={workflow3EditMode}
                                        onChange={(event) => setWorkflow3EditMode(event.target.checked)}
                                        disabled={loading || linesLoading}
                                    />
                                )}
                                label="Edit bill"
                            />
                        </Box>
                    </Grid>
                )}
                <Grid item xs={12} sm={6}>
                    <TextField
                        label="ABN"
                        value={abn}
                        onChange={(e) => setAbn(e.target.value)}
                        fullWidth
                        error={hasAbnMismatch}
                        helperText={hasAbnMismatch ? 'ABN does not match the selected vendor.' : undefined}
                        disabled={!canEditLines || loading}
                        InputLabelProps={{ shrink: true }}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        label="Supplier Reference"
                        value={supplierRef}
                        onChange={(e) => setSupplierRef(e.target.value)}
                        fullWidth
                        disabled={!canEditLines || loading}
                        InputLabelProps={{ shrink: true }}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        label="Description"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        fullWidth
                        multiline
                        minRows={2}
                        disabled={!canEditDescription || loading}
                        InputLabelProps={{ shrink: true }}
                        helperText={isWorkflow3 ? 'Required on Awaiting Approval bills.' : ' '}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                 <Autocomplete
                        disablePortal
                        options={vendors}
                        value={getOptionById(vendors, vendorId)}
                        onChange={async (event, newValue) => {
                            await handleVendorChange(newValue);
                        }}
                        getOptionLabel={getVendorOptionLabel}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        loading={selectorsLoading}
                        disabled={!canEditLines || loading || selectorsLoading}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Vendor"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                helperText={!vendorId ? 'If the vendor is missing, create it in MYOB first.' : ' '}
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        label="Net"
                        type="number"
                        value={net}
                        onChange={(e) => setNet(e.target.value)}
                        fullWidth
                        required
                        disabled={!canEditLines || loading}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ step: '0.01' }}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        label="Tax"
                        type="number"
                        value={tax}
                        onChange={(e) => setTax(e.target.value)}
                        fullWidth
                        required
                        disabled={!canEditLines || loading}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ step: '0.01' }}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <DatePicker
                        label="Invoice Date"
                        date={invoiceDate}
                        setDate={canEditLines ? setInvoiceDate : () => { }}
                        disabled={!canEditLines || loading}
                        fullWidth
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <DatePicker
                        label="Due Date"
                        date={dueDate}
                        setDate={canEditLines ? setDueDate : () => { }}
                        disabled={!canEditLines || loading}
                        fullWidth
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Button
                            type="button"
                            variant="outlined"
                            onClick={openPurchaseReceiptModal}
                            disabled={!canEditLines || loading || linesLoading || !vendorId || purchaseReceiptsLoading}
                        >
                            {purchaseReceiptsLoading ? 'Loading receipts...' : (hasMatchedPurchaseReceipts ? `PRs: ${matchedReceiptMyobIds.length}` : 'Match PRs')}
                        </Button>
                        {hasMatchedPurchaseReceipts && (
                            <>
                                <Typography variant="body2">Matched: <strong>{matchedReceiptMyobIds.join(', ')}</strong></Typography>
                                <Button
                                    type="button"
                                    variant="text"
                                    size="small"
                                    color="secondary"
                                    onClick={() => setMatchedReceiptIds([])}
                                    disabled={!canEditLines || loading || linesLoading}
                                >
                                    Clear
                                </Button>
                            </>
                        )}
                    </Box>
                </Grid>
                <Grid item xs={12}>
                    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', flex: '1 1 520px' }}>
                                <Typography variant="h6">Bill Lines</Typography>
                                <Autocomplete
                                    disablePortal
                                    options={roles}
                                    value={getOptionById(roles, approverRoleId)}
                                    onChange={(event, newValue) => {
                                        approverManuallySelectedRef.current = true;
                                        setApproverRoleId(newValue?.id ?? null);
                                    }}
                                    getOptionLabel={getRoleOptionLabel}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    loading={selectorsLoading}
                                    disabled={!canEditLines || loading || selectorsLoading}
                                    sx={{ minWidth: 320, maxWidth: 420 }}
                                    renderInput={(params) => <TextField {...params} label="Approver" size="small" />}
                                />
                            </Box>
                            <Button
                                type="button"
                                variant="outlined"
                                color="primary"
                                onClick={handleOpenLineEditor}
                                disabled={!canEditLines || loading || linesLoading}
                            >
                                Edit Lines
                            </Button>
                        </Box>

                        {hasMatchedPurchaseReceipts ? (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Matched purchase receipt lines stay read-only. Use Edit Lines to add PR additional lines that will be posted after the matched purchase receipts.
                            </Typography>
                        ) : (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Account and sub account stay editable here. Use Edit Lines for full line editing and to open the bill PDF in a separate window.
                            </Typography>
                        )}

                        <Grid container spacing={2} sx={{ mb: 2 }}>
                            <Grid item xs={12} md={4}>
                                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 1.5 }}>
                                    <Typography variant="caption" color="text.secondary">Line count</Typography>
                                    <Typography variant="h6">{lineCount}</Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 1.5 }}>
                                    <Typography variant="caption" color="text.secondary">Quantity total</Typography>
                                    <Typography variant="h6">{formatSummaryNumber(quantityTotal)}</Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Box sx={{ border: '1px solid', borderColor: showLineTotalWarning ? 'error.main' : 'divider', bgcolor: showLineTotalWarning ? 'error.light' : 'transparent', borderRadius: 1.5, p: 1.5 }}>
                                    <Typography variant="caption" color="text.secondary">Line total</Typography>
                                    <Typography variant="h6">{formatCurrencyWithNumberFormat(lineValueTotal)}</Typography>
                                    {billTotal !== null && (
                                        <Typography variant="caption" color={showLineTotalWarning ? 'error.main' : 'text.secondary'}>
                                            Bill total: {formatCurrencyWithNumberFormat(billTotal)}
                                        </Typography>
                                    )}
                                   
                                </Box>
                            </Grid>
                        </Grid>

                        {(!hasMatchedPurchaseReceipts || editableBillLines.length > 0) && (
                            <Grid container spacing={2} sx={{ mb: 2 }}>
                                <Grid item xs={12} md={4}>
                                    <Autocomplete
                                        disablePortal
                                        options={accounts}
                                        value={getOptionById(accounts, bulkAccountId)}
                                        onChange={(event, newValue) => {
                                            const nextAccountId = newValue?.id ?? null;
                                            setBulkAccountId(nextAccountId);
                                            setBillLines((currentLines) => currentLines.map((line) => ({
                                                ...line,
                                                accountId: hasMatchedPurchaseReceipts && !line.prAdditional ? line.accountId : nextAccountId,
                                            })));
                                        }}
                                        filterOptions={filterAccountingOptions}
                                        getOptionLabel={getAccountingOptionLabel}
                                        renderOption={renderAccountingOption}
                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                        loading={selectorsLoading}
                                        disabled={!canEditLines || loading || linesLoading || selectorsLoading}
                                        renderInput={(params) => <TextField {...params} label="Account for all lines" />}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Autocomplete
                                        disablePortal
                                        options={subAccounts}
                                        value={getOptionById(subAccounts, bulkSubAccountId)}
                                        onChange={(event, newValue) => {
                                            const nextSubAccountId = newValue?.id ?? null;
                                            setBulkSubAccountId(nextSubAccountId);
                                            setBillLines((currentLines) => currentLines.map((line) => ({
                                                ...line,
                                                subAccountId: hasMatchedPurchaseReceipts && !line.prAdditional ? line.subAccountId : nextSubAccountId,
                                            })));
                                        }}
                                        filterOptions={filterAccountingOptions}
                                        getOptionLabel={getAccountingOptionLabel}
                                        renderOption={renderAccountingOption}
                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                        loading={selectorsLoading}
                                        disabled={!canEditLines || loading || linesLoading || selectorsLoading}
                                        renderInput={(params) => <TextField {...params} label="Sub Account for all lines" />}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                        <TextField
                                            label="Additional tax %"
                                            type="number"
                                            value={additionalTaxPercent}
                                            onChange={(e) => setAdditionalTaxPercent(e.target.value)}
                                            disabled={!canEditLines || loading || linesLoading}
                                            size="small"
                                            inputProps={{ min: '0', step: '0.01' }}
                                        />
                                        <Button
                                            type="button"
                                            variant="outlined"
                                            onClick={applyAdditionalTaxToLines}
                                            disabled={!canEditLines || loading || linesLoading}
                                        >
                                            Add
                                        </Button>
                                    </Box>
                                </Grid>
                            </Grid>
                        )}

                        <Box onScroll={handlePreviewLineScroll} sx={{ maxHeight: 360, overflowX: 'auto', overflowY: 'auto', pr: 1 }}>
                            {hasMatchedPurchaseReceipts ? (
                                <>
                                    {matchedPRDetailsLoading && (
                                        <Typography variant="body2">Loading purchase receipt lines...</Typography>
                                    )}

                                    {!matchedPRDetailsLoading && matchedPRDetails.length === 0 && (
                                        <Typography variant="body2">No lines found for the matched purchase receipts.</Typography>
                                    )}

                                    {!matchedPRDetailsLoading && matchedPRDetails.length > 0 && matchedPRDetails.map((receipt) => (
                                        <Box key={receipt.id} sx={{ minWidth: 600, mb: 3 }}>
                                            <Typography variant="subtitle1" sx={{ px: 1, pb: 1 }}>
                                                {receipt.myobid}
                                            </Typography>
                                            <Box
                                                sx={{
                                                    display: 'grid',
                                                    gridTemplateColumns: '60px 1fr 100px 120px 120px',
                                                    gap: 2,
                                                    px: 1,
                                                    pb: 1,
                                                    borderBottom: '1px solid',
                                                    borderColor: 'divider',
                                                    alignItems: 'center'
                                                }}
                                            >
                                                <Typography variant="caption" color="text.secondary">Line</Typography>
                                                <Typography variant="caption" color="text.secondary">Description</Typography>
                                                <Typography variant="caption" color="text.secondary">Qty</Typography>
                                                <Typography variant="caption" color="text.secondary">Unit Cost</Typography>
                                                <Typography variant="caption" color="text.secondary">Total</Typography>
                                            </Box>

                                            {(receipt.lines ?? []).map((line, index) => (
                                                <Box
                                                    key={`${receipt.id}-${line.id}`}
                                                    sx={{
                                                        display: 'grid',
                                                        gridTemplateColumns: '60px 1fr 100px 120px 120px',
                                                        gap: 2,
                                                        px: 1,
                                                        py: 1.5,
                                                        borderBottom: '1px solid',
                                                        borderColor: 'divider',
                                                        alignItems: 'center'
                                                    }}
                                                >
                                                    <Typography variant="body2">{line.lineNbr ?? index + 1}</Typography>
                                                    <Typography variant="body2" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={line.stockItemDescription}>
                                                        {line.stockItemDescription || '-'}
                                                    </Typography>
                                                    <Typography variant="body2">{line.quantity ?? '-'}</Typography>
                                                    <Typography variant="body2">{line.unitCost != null ? formatCurrencyWithNumberFormat(line.unitCost) : '-'}</Typography>
                                                    <Typography variant="body2">{line.quantity != null && line.unitCost != null ? formatCurrencyWithNumberFormat(line.quantity * line.unitCost) : '-'}</Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                    ))}

                                    <Box sx={{ minWidth: 1250 }}>
                                        <Typography variant="subtitle1" sx={{ mb: 1 }}>PR Additional Lines</Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                            These bill-only lines are stored separately from OCR lines and are posted after the matched purchase receipts.
                                        </Typography>

                                        {linesLoading && (
                                            <Typography variant="body2">Loading PR additional lines...</Typography>
                                        )}

                                        {!linesLoading && editableBillLines.length === 0 && (
                                            <Typography variant="body2">No PR additional lines have been added yet.</Typography>
                                        )}

                                        {!linesLoading && editableBillLines.length > 0 && (
                                            <>
                                                <Box
                                                    sx={{
                                                        display: 'grid',
                                                        gridTemplateColumns: billLineGridColumns,
                                                        gap: 2,
                                                        px: 1,
                                                        pb: 1,
                                                        borderBottom: '1px solid',
                                                        borderColor: 'divider',
                                                        alignItems: 'center'
                                                    }}
                                                >
                                                    <Typography variant="caption" color="text.secondary">Line</Typography>
                                                    <Typography variant="caption" color="text.secondary">Description</Typography>
                                                    <Typography variant="caption" color="text.secondary">Qty</Typography>
                                                    <Typography variant="caption" color="text.secondary">Unit</Typography>
                                                    <Typography variant="caption" color="text.secondary">Total</Typography>
                                                    <Typography variant="caption" color="text.secondary">Account</Typography>
                                                    <Typography variant="caption" color="text.secondary">Sub Account</Typography>
                                                    <Typography variant="caption" color="text.secondary">GST Free</Typography>
                                                </Box>

                                                {previewEditableBillLines.map((line, index) => {
                                                    const lineMismatch = getBillLineMismatchDetails(line);

                                                    return (
                                                        <Box
                                                            key={line.localId}
                                                            sx={{
                                                                px: 1,
                                                                py: 1.5,
                                                                borderBottom: '1px solid',
                                                                borderColor: lineMismatch ? 'error.main' : 'divider',
                                                                bgcolor: lineMismatch ? 'rgba(211, 47, 47, 0.08)' : 'transparent'
                                                            }}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    display: 'grid',
                                                                    gridTemplateColumns: billLineGridColumns,
                                                                    gap: 2,
                                                                    alignItems: 'center'
                                                                }}
                                                            >
                                                                <Typography variant="body2" color={lineMismatch ? 'error.main' : 'text.primary'}>{index + 1}</Typography>
                                                                <Typography variant="body2" color={lineMismatch ? 'error.main' : 'text.primary'} sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={line.description}>
                                                                    {line.description || '-'}
                                                                </Typography>
                                                                <Typography variant="body2" color={lineMismatch ? 'error.main' : 'text.primary'}>{parseDecimal(line.quantity) ?? '-'}</Typography>
                                                                <Typography variant="body2" color={lineMismatch ? 'error.main' : 'text.primary'}>{parseDecimal(line.unitPrice) !== null ? formatBillLineUnitPrice(parseDecimal(line.unitPrice)) : '-'}</Typography>
                                                                <Typography variant="body2" color={lineMismatch ? 'error.main' : 'text.primary'}>{parseDecimal(line.totalPrice) !== null ? formatCurrencyWithNumberFormat(parseDecimal(line.totalPrice)) : '-'}</Typography>
                                                                <Autocomplete
                                                                    disablePortal
                                                                    options={accounts}
                                                                    value={getOptionById(accounts, line.accountId)}
                                                                    onChange={(event, newValue) => updateBillLine(line.localId, 'accountId', newValue?.id ?? null)}
                                                                    filterOptions={filterAccountingOptions}
                                                                    getOptionLabel={getAccountingOptionLabel}
                                                                    renderOption={renderAccountingOption}
                                                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                                                    loading={selectorsLoading}
                                                                    disabled={!canEditLines || loading || selectorsLoading}
                                                                    renderInput={(params) => <TextField {...params} label="Account" size="small" />}
                                                                />
                                                                <Autocomplete
                                                                    disablePortal
                                                                    options={subAccounts}
                                                                    value={getOptionById(subAccounts, line.subAccountId)}
                                                                    onChange={(event, newValue) => updateBillLine(line.localId, 'subAccountId', newValue?.id ?? null)}
                                                                    filterOptions={filterAccountingOptions}
                                                                    getOptionLabel={getAccountingOptionLabel}
                                                                    renderOption={renderAccountingOption}
                                                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                                                    loading={selectorsLoading}
                                                                    disabled={!canEditLines || loading || selectorsLoading}
                                                                    renderInput={(params) => <TextField {...params} label="Sub Account" size="small" />}
                                                                />
                                                                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                                                    <Checkbox
                                                                        checked={Boolean(line.isGSTFree)}
                                                                        onChange={(event) => updateBillLine(line.localId, 'isGSTFree', event.target.checked)}
                                                                        disabled={!canEditLines || loading}
                                                                        size="small"
                                                                    />
                                                                </Box>
                                                            </Box>
                                                            {lineMismatch && (
                                                                <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 1 }}>
                                                                    {lineMismatch.message}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    );
                                                })}

                                                {editableBillLines.length > previewEditableBillLines.length && (
                                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 1, pt: 1 }}>
                                                        <Typography variant="caption" color="text.secondary">
                                                            Showing {previewEditableBillLines.length} of {editableBillLines.length} lines.
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </>
                                        )}
                                    </Box>
                                </>
                            ) : (
                                <>
                                    {linesLoading && (
                                        <Typography variant="body2">Loading bill lines...</Typography>
                                    )}

                                    {!linesLoading && billLines.length === 0 && (
                                        <Typography variant="body2">No bill lines were found for this bill.</Typography>
                                    )}

                                    {!linesLoading && billLines.length > 0 && (
                                        <Box sx={{ minWidth: 1250 }}>
                                            <Box
                                                sx={{
                                                    display: 'grid',
                                                    gridTemplateColumns: billLineGridColumns,
                                                    gap: 2,
                                                    px: 1,
                                                    pb: 1,
                                                    borderBottom: '1px solid',
                                                    borderColor: 'divider',
                                                    alignItems: 'center'
                                                }}
                                            >
                                                <Typography variant="caption" color="text.secondary">Line</Typography>
                                                <Typography variant="caption" color="text.secondary">Description</Typography>
                                                <Typography variant="caption" color="text.secondary">Qty</Typography>
                                                <Typography variant="caption" color="text.secondary">Unit</Typography>
                                                <Typography variant="caption" color="text.secondary">Total</Typography>
                                                <Typography variant="caption" color="text.secondary">Account</Typography>
                                                <Typography variant="caption" color="text.secondary">Sub Account</Typography>
                                                <Typography variant="caption" color="text.secondary">GST Free</Typography>
                                            </Box>

                                            {previewBillLines.map((line, index) => {
                                                const lineMismatch = getBillLineMismatchDetails(line);

                                                return (
                                                    <Box
                                                        key={line.localId}
                                                        sx={{
                                                            px: 1,
                                                            py: 1.5,
                                                            borderBottom: '1px solid',
                                                            borderColor: lineMismatch ? 'error.main' : 'divider',
                                                            bgcolor: lineMismatch ? 'rgba(211, 47, 47, 0.08)' : 'transparent'
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                display: 'grid',
                                                                gridTemplateColumns: billLineGridColumns,
                                                                gap: 2,
                                                                alignItems: 'center'
                                                            }}
                                                        >
                                                            <Typography variant="body2" color={lineMismatch ? 'error.main' : 'text.primary'}>{index + 1}</Typography>
                                                            <Typography variant="body2" color={lineMismatch ? 'error.main' : 'text.primary'} sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={line.description}>
                                                                {line.description || '-'}
                                                            </Typography>
                                                            <Typography variant="body2" color={lineMismatch ? 'error.main' : 'text.primary'}>{parseDecimal(line.quantity) ?? '-'}</Typography>
                                                            <Typography variant="body2" color={lineMismatch ? 'error.main' : 'text.primary'}>{parseDecimal(line.unitPrice) !== null ? formatBillLineUnitPrice(parseDecimal(line.unitPrice)) : '-'}</Typography>
                                                            <Typography variant="body2" color={lineMismatch ? 'error.main' : 'text.primary'}>{parseDecimal(line.totalPrice) !== null ? formatCurrencyWithNumberFormat(parseDecimal(line.totalPrice)) : '-'}</Typography>
                                                            <Autocomplete
                                                                disablePortal
                                                                options={accounts}
                                                                value={getOptionById(accounts, line.accountId)}
                                                                onChange={(event, newValue) => updateBillLine(line.localId, 'accountId', newValue?.id ?? null)}
                                                                filterOptions={filterAccountingOptions}
                                                                getOptionLabel={getAccountingOptionLabel}
                                                                renderOption={renderAccountingOption}
                                                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                                                loading={selectorsLoading}
                                                                disabled={!canEditLines || loading || selectorsLoading}
                                                                renderInput={(params) => <TextField {...params} label="Account" size="small" />}
                                                            />
                                                            <Autocomplete
                                                                disablePortal
                                                                options={subAccounts}
                                                                value={getOptionById(subAccounts, line.subAccountId)}
                                                                onChange={(event, newValue) => updateBillLine(line.localId, 'subAccountId', newValue?.id ?? null)}
                                                                filterOptions={filterAccountingOptions}
                                                                getOptionLabel={getAccountingOptionLabel}
                                                                renderOption={renderAccountingOption}
                                                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                                                loading={selectorsLoading}
                                                                disabled={!canEditLines || loading || selectorsLoading}
                                                                renderInput={(params) => <TextField {...params} label="Sub Account" size="small" />}
                                                            />
                                                            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                                                <Checkbox
                                                                    checked={Boolean(line.isGSTFree)}
                                                                    onChange={(event) => updateBillLine(line.localId, 'isGSTFree', event.target.checked)}
                                                                    disabled={!canEditLines || loading}
                                                                    size="small"
                                                                />
                                                            </Box>
                                                        </Box>
                                                        {lineMismatch && (
                                                            <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 1 }}>
                                                                {lineMismatch.message}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                );
                                            })}

                                            {billLines.length > previewBillLines.length && (
                                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 1, pt: 1 }}>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Showing {previewBillLines.length} of {billLines.length} lines.
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>
                                    )}
                                </>
                            )}
                        </Box>
                    </Box>
                </Grid>
                <Grid item xs={12} display="flex" justifyContent="flex-end" gap={1}>
                    {props.onCancel && (
                        <Button
                            type="button"
                            variant="outlined"
                            color="secondary"
                            onClick={props.onCancel}
                            disabled={loading}
                        >
                            Cancel
                        </Button>
                    )}
                    {!isWorkflow3 && (
                        <>
                            <Button
                                type="submit"
                                variant="outlined"
                                color="primary"
                                disabled={!canEditLines || loading || linesLoading}
                            >
                                {loading && !submittingForApproval ? 'Saving...' : 'Save'}
                            </Button>
                            <Button
                                type="button"
                                variant="contained"
                                color="primary"
                                onClick={handleSaveAndSubmitForApproval}
                                disabled={!canEditLines || loading || linesLoading}
                            >
                                {loading && submittingForApproval ? 'Submitting...' : 'Save & Submit for Approval'}
                            </Button>
                            {hasMatchedPurchaseReceipts && (
                                <Button
                                    type="button"
                                    variant="contained"
                                    color="success"
                                    onClick={handlePushToMyob}
                                    disabled={!canEditLines || loading || linesLoading || pushingToMyob}
                                >
                                    {pushingToMyob ? 'Pushing to MYOB...' : 'Push to MYOB'}
                                </Button>
                            )}
                        </>
                    )}
                    {isWorkflow3 && !workflow3EditMode && (
                        <>
                            <Button
                                type="button"
                                variant="outlined"
                                color="error"
                                onClick={() => handleWorkflowDecision('reject')}
                                disabled={loading || linesLoading}
                            >
                                {loading && workflowAction === 'reject' ? 'Rejecting...' : 'Reject'}
                            </Button>
                            <Button
                                type="button"
                                variant="contained"
                                color="success"
                                onClick={() => handleWorkflowDecision('approve')}
                                disabled={loading || linesLoading}
                            >
                                {loading && workflowAction === 'approve' ? 'Approving...' : 'Approve'}
                            </Button>
                        </>
                    )}
                    {isWorkflow3 && workflow3EditMode && (
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={!canEditLines || loading || linesLoading}
                        >
                            {loading ? 'Saving...' : 'Save'}
                        </Button>
                    )}
                </Grid>
                {isWorkflow4 && approvedByUser && (
                    <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'right', mt: 1 }}>
                            Approved by: {approvedByUser}
                        </Typography>
                    </Grid>
                )}
            </Grid>

            <Modal
                open={purchaseReceiptModalOpen}
                onClose={closePurchaseReceiptModal}
                aria-labelledby="purchase-receipt-picker-title"
            >
                <Box sx={{ ...modalStyle, maxWidth: 1400 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Typography id="purchase-receipt-picker-title" variant="h5">Select Purchase Receipts</Typography>
                        <IconButton aria-label="close" onClick={closePurchaseReceiptModal}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Grid container spacing={2}>
                        <Grid item xs={12} md={5}>
                            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1, maxHeight: '65vh', overflowY: 'auto' }}>
                                {purchaseReceipts.length === 0 && (
                                    <Typography variant="body2" sx={{ p: 1 }}>
                                        No receipts found for this vendor.
                                    </Typography>
                                )}

                                {purchaseReceipts.map((receipt) => (
                                    <Box
                                        key={receipt.id}
                                        onClick={() => setSelectedReceiptId(receipt.id)}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            justifyContent: 'space-between',
                                            gap: 1,
                                            border: '1px solid',
                                            borderColor: selectedReceiptId === receipt.id ? 'primary.main' : 'divider',
                                            bgcolor: pendingMatchedReceiptIds.includes(receipt.id) ? 'action.selected' : 'background.paper',
                                            borderRadius: 1,
                                            px: 1.5,
                                            py: 1,
                                            mb: 0.75,
                                            cursor: 'pointer'
                                        }}
                                    >
                                        <Box sx={{ textAlign: 'left', flex: 1 }}>
                                            <Typography variant="body2"><strong>{receipt.myobid}</strong></Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {dayjs(receipt.date).format('DD/MM/YYYY')} | {formatCurrencyWithNumberFormat(receipt.totalCost ?? 0)}
                                            </Typography>
                                            {receipt.supplierRef && (
                                                <Typography variant="caption" display="block" color="text.secondary">
                                                    Ref: {receipt.supplierRef}
                                                </Typography>
                                            )}
                                        </Box>
                                        <Checkbox
                                            checked={pendingMatchedReceiptIds.includes(receipt.id)}
                                            onClick={(event) => event.stopPropagation()}
                                            onChange={() => togglePendingPurchaseReceipt(receipt.id)}
                                        />
                                    </Box>
                                ))}
                            </Box>
                        </Grid>

                        <Grid item xs={12} md={7}>
                            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2, maxHeight: '65vh', overflowY: 'auto' }}>
                                {!selectedReceiptId && (
                                    <Typography variant="body2" color="text.secondary">
                                        Select a purchase receipt to preview details.
                                    </Typography>
                                )}

                                {selectedReceiptId && receiptDetailsLoading && (
                                    <Typography variant="body2">Loading receipt details...</Typography>
                                )}

                                {selectedReceiptId && !receiptDetailsLoading && selectedReceiptDetails && (
                                    <>
                                        <Typography variant="h6" sx={{ mb: 1 }}>{selectedReceiptDetails.myobid}</Typography>
                                        <Grid container spacing={1} sx={{ mb: 2 }}>
                                            <Grid item xs={12} sm={6}><Typography variant="body2"><strong>Date:</strong> {dayjs(selectedReceiptDetails.date).format('DD/MM/YYYY')}</Typography></Grid>
                                            <Grid item xs={12} sm={6}><Typography variant="body2"><strong>Status:</strong> {selectedReceiptDetails.status}</Typography></Grid>
                                            <Grid item xs={12} sm={6}><Typography variant="body2"><strong>Supplier:</strong> {selectedReceiptDetails.supplierName || '-'}</Typography></Grid>
                                            <Grid item xs={12} sm={6}><Typography variant="body2"><strong>PO:</strong> {selectedReceiptDetails.purchaseOrderId}</Typography></Grid>
                                            <Grid item xs={12} sm={6}><Typography variant="body2"><strong>Total Qty:</strong> {selectedReceiptDetails.totalQty}</Typography></Grid>
                                            <Grid item xs={12} sm={6}><Typography variant="body2"><strong>Total Cost:</strong> {formatCurrencyWithNumberFormat(selectedReceiptDetails.totalCost ?? 0)}</Typography></Grid>
                                            <Grid item xs={12}><Typography variant="body2"><strong>Description:</strong> {selectedReceiptDetails.description || '-'}</Typography></Grid>
                                        </Grid>

                                        <Typography variant="subtitle1" sx={{ mb: 1 }}>Lines</Typography>
                                        <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
                                            {(selectedReceiptDetails.lines ?? []).map((line) => (
                                                <Box key={line.id} sx={{ py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                                                    <Typography variant="body2">
                                                        <strong>#{line.lineNbr}</strong> {line.stockItemMyobid || ''} {line.stockItemDescription || ''}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Qty: {line.quantity} | Unit Cost: {formatCurrencyWithNumberFormat(line.unitCost ?? 0)} | Lot: {line.lotSerialNumber || '-'}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                    </>
                                )}
                            </Box>
                        </Grid>
                    </Grid>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                        <Button type="button" variant="text" onClick={closePurchaseReceiptModal}>Cancel</Button>
                        <Button type="button" variant="contained" onClick={applySelectedPurchaseReceipt}>Use Selected Receipt(s)</Button>
                    </Box>
                </Box>
            </Modal>

            <Modal
                open={lineEditorOpen}
                onClose={handleCloseLineEditor}
                aria-labelledby="bill-line-editor-title"
            >
                <Box sx={modalStyle}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Typography id="bill-line-editor-title" variant="h5">Edit Lines</Typography>
                        <IconButton aria-label="close" onClick={handleCloseLineEditor}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        The bill PDF opens in a separate window. Update any editable line fields here, then save the bill from the main form.
                    </Typography>

                    {hasMatchedPurchaseReceipts && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Only PR additional lines are editable while purchase receipts are matched.
                        </Typography>
                    )}

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                        <Button
                            type="button"
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={addBillLine}
                            disabled={!canEditLines || loading || selectorsLoading}
                        >
                            Add Line
                        </Button>
                    </Box>

                    <Box onScroll={handleEditorLineScroll} sx={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '65vh', pr: 1 }}>
                        <Box sx={{ minWidth: 1560 }}>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: billLineEditorGridColumns,
                                    gap: 2,
                                    px: 1,
                                    pb: 1,
                                    borderBottom: '1px solid',
                                    borderColor: 'divider',
                                    alignItems: 'center'
                                }}
                            >
                                <Typography variant="caption" color="text.secondary">Line</Typography>
                                <Typography variant="caption" color="text.secondary">Description</Typography>
                                <Typography variant="caption" color="text.secondary">Qty</Typography>
                                <Typography variant="caption" color="text.secondary">Unit Price</Typography>
                                <Typography variant="caption" color="text.secondary">Total Price</Typography>
                                <Typography variant="caption" color="text.secondary">Account</Typography>
                                <Typography variant="caption" color="text.secondary">Sub Account</Typography>
                                <Typography variant="caption" color="text.secondary">GST Free</Typography>
                                <Typography variant="caption" color="text.secondary">Actions</Typography>
                            </Box>

                            {visibleEditorBillLines.map((line, index) => {
                                const lineMismatch = getBillLineMismatchDetails(line);

                                return (
                                    <Box
                                        key={`modal-${line.localId}`}
                                        sx={{
                                            px: 1,
                                            py: 1.5,
                                            borderBottom: '1px solid',
                                            borderColor: lineMismatch ? 'error.main' : 'divider',
                                            bgcolor: lineMismatch ? 'rgba(211, 47, 47, 0.08)' : 'transparent'
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'grid',
                                                gridTemplateColumns: billLineEditorGridColumns,
                                                gap: 2,
                                                alignItems: 'center'
                                            }}
                                        >
                                            <Typography variant="body2" color={lineMismatch ? 'error.main' : 'text.primary'}>{index + 1}</Typography>
                                            <TextField
                                                label="Description"
                                                value={line.description}
                                                onChange={(e) => updateBillLine(line.localId, 'description', e.target.value)}
                                                fullWidth
                                                size="small"
                                                disabled={!canEditLines || loading}
                                                InputLabelProps={{ shrink: true }}
                                            />
                                            <TextField
                                                label="Quantity"
                                                type="number"
                                                value={line.quantity}
                                                onChange={(e) => updateBillLine(line.localId, 'quantity', e.target.value)}
                                                fullWidth
                                                size="small"
                                                disabled={!canEditLines || loading}
                                                InputLabelProps={{ shrink: true }}
                                                inputProps={{ step: '1' }}
                                            />
                                            <TextField
                                                label="Unit Price"
                                                type="number"
                                                value={line.unitPrice}
                                                onChange={(e) => updateBillLine(line.localId, 'unitPrice', e.target.value)}
                                                fullWidth
                                                size="small"
                                                disabled={!canEditLines || loading}
                                                InputLabelProps={{ shrink: true }}
                                                inputProps={{ step: '0.0001' }}
                                            />
                                            <TextField
                                                label="Total Price"
                                                type="number"
                                                value={line.totalPrice}
                                                onChange={(e) => updateBillLine(line.localId, 'totalPrice', e.target.value)}
                                                fullWidth
                                                size="small"
                                                disabled={!canEditLines || loading}
                                                InputLabelProps={{ shrink: true }}
                                                inputProps={{ step: '1' }}
                                            />
                                            <Autocomplete
                                                disablePortal
                                                options={accounts}
                                                value={getOptionById(accounts, line.accountId)}
                                                onChange={(event, newValue) => updateBillLine(line.localId, 'accountId', newValue?.id ?? null)}
                                                filterOptions={filterAccountingOptions}
                                                getOptionLabel={getAccountingOptionLabel}
                                                renderOption={renderAccountingOption}
                                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                                loading={selectorsLoading}
                                                disabled={!canEditLines || loading || selectorsLoading}
                                                renderInput={(params) => <TextField {...params} label="Account" size="small" />}
                                            />
                                            <Autocomplete
                                                disablePortal
                                                options={subAccounts}
                                                value={getOptionById(subAccounts, line.subAccountId)}
                                                onChange={(event, newValue) => updateBillLine(line.localId, 'subAccountId', newValue?.id ?? null)}
                                                filterOptions={filterAccountingOptions}
                                                getOptionLabel={getAccountingOptionLabel}
                                                renderOption={renderAccountingOption}
                                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                                loading={selectorsLoading}
                                                disabled={!canEditLines || loading || selectorsLoading}
                                                renderInput={(params) => <TextField {...params} label="Sub Account" size="small" />}
                                            />
                                            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                                <Checkbox
                                                    checked={Boolean(line.isGSTFree)}
                                                    onChange={(event) => updateBillLine(line.localId, 'isGSTFree', event.target.checked)}
                                                    disabled={!canEditLines || loading}
                                                    size="small"
                                                />
                                            </Box>
                                            <Box display="flex" justifyContent="center">
                                                <IconButton
                                                    aria-label="delete line"
                                                    color="error"
                                                    onClick={() => deleteBillLine(line.localId)}
                                                    disabled={!canEditLines || loading || (!hasMatchedPurchaseReceipts && billLines.length <= 1)}
                                                    size="small"
                                                >
                                                    <DeleteOutlineIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        </Box>
                                        {lineMismatch && (
                                            <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 1 }}>
                                                {lineMismatch.message}
                                            </Typography>
                                        )}
                                    </Box>
                                );
                            })}

                            {editableBillLines.length > visibleEditorBillLines.length && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        Showing {visibleEditorBillLines.length} of {editableBillLines.length} lines.
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                        <Button
                            type="button"
                            variant="contained"
                            onClick={handleCloseLineEditor}
                        >
                            Done
                        </Button>
                    </Box>
                </Box>
            </Modal>
        </Box>
    );
};

export default EditBill;